<template>
  <div class="global-container">
    <div class="content">
    <!-- Header -->
    <div class="header-section">
      <div class="title-section">
        <h1 class="page-title">Categories</h1>
        <p class="page-description">Organize expenses by category</p>
      </div>
      <button class="add-button" @click="openCategoryModal">
        <span class="plus-icon">+</span>
        Add Category
      </button>
    </div>

    <!-- Stat Cards -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-header">
          <span class="stat-label">Total Categories</span>
          <span class="stat-icon">&#x1F4CA;</span>
        </div>
        <div class="stat-value">{{ totalCategories }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-header">
          <span class="stat-label">Total Vendors</span>
          <span class="stat-icon">&#x1F3E2;</span>
        </div>
        <div class="stat-value">{{ totalVendors }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-header">
          <span class="stat-label">Total Expenses</span>
          <span class="stat-icon">&#x1F4B2;</span>
        </div>
        <div class="stat-value">${{ totalExpenses.toLocaleString() }}</div>
      </div>
    </div>

    <!-- Tabs -->
    <div class="tabs-section">
      <button
        v-for="tab in tabs"
        :key="tab.route"
        :class="['tab-btn', { active: activeTab === tab.name }]"
        @click="navigateToTab(tab.route)"
      >
        {{ tab.name }}
      </button>
    </div>

    <!-- Bulk Upload Card -->
    <div class="bulk-card">
      <div class="bulk-card-header">
        <div>
          <span class="bulk-card-title">Bulk Upload Expenses</span>
          <span class="bulk-card-subtitle">Upload an Excel file to create Categories, Vendors, Expenses &amp; Payments at once</span>
        </div>
        <a href="/templates/bulk-upload-template.xlsx" download="bulk-upload-template.xlsx" class="download-btn">
          &#x2B07; Download Sample File
        </a>
      </div>

      <!-- Drop zone -->
      <div
        class="drop-zone"
        :class="{ 'drop-zone--active': isDragging, 'drop-zone--has-file': selectedFile }"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="onFileDrop"
        @click="$refs.fileInput.click()"
      >
        <input
          ref="fileInput"
          type="file"
          accept=".xlsx,.xls"
          style="display:none"
          @change="onFileChange"
        />
        <template v-if="!selectedFile">
          <span class="drop-icon">&#x1F4C2;</span>
          <span class="drop-text">Click to browse or drag &amp; drop your Excel file here</span>
          <span class="drop-hint">.xlsx or .xls only</span>
        </template>
        <template v-else>
          <span class="drop-icon">&#x1F4C4;</span>
          <span class="drop-text file-name">{{ selectedFile.name }}</span>
          <span class="drop-hint">{{ (selectedFile.size / 1024).toFixed(1) }} KB &mdash; click to change</span>
        </template>
      </div>

      <!-- Actions -->
      <div class="bulk-actions" v-if="selectedFile">
        <button class="btn-clear" @click.stop="clearFile">Clear</button>
        <button class="btn-upload" @click.stop="uploadFile" :disabled="uploading">
          <span v-if="uploading">Uploading...</span>
          <span v-else>Upload &amp; Process</span>
        </button>
      </div>

      <!-- Result Panel -->
      <div v-if="uploadResult" class="result-panel" :class="hasUploadErrors ? 'result-panel--warn' : 'result-panel--success'">
        <!-- Summary counts -->
        <div v-if="uploadResult.summary" class="result-summary">
          <div class="result-summary-title">Upload complete</div>
          <div class="result-summary-grid">
            <div class="result-chip result-chip--green">&#x2714; {{ uploadResult.summary.categoriesCreated }} categor{{ uploadResult.summary.categoriesCreated === 1 ? 'y' : 'ies' }} created</div>
            <div v-if="uploadResult.summary.categoriesSkipped" class="result-chip result-chip--grey">{{ uploadResult.summary.categoriesSkipped }} skipped</div>
            <div class="result-chip result-chip--green">&#x2714; {{ uploadResult.summary.vendorsCreated }} vendor{{ uploadResult.summary.vendorsCreated === 1 ? '' : 's' }} created</div>
            <div v-if="uploadResult.summary.vendorsSkipped" class="result-chip result-chip--grey">{{ uploadResult.summary.vendorsSkipped }} skipped</div>
            <div class="result-chip result-chip--green">&#x2714; {{ uploadResult.summary.expensesCreated }} expense{{ uploadResult.summary.expensesCreated === 1 ? '' : 's' }} created</div>
            <div v-if="uploadResult.summary.expensesSkipped" class="result-chip result-chip--grey">{{ uploadResult.summary.expensesSkipped }} skipped</div>
            <div class="result-chip result-chip--green">&#x2714; {{ uploadResult.summary.paymentsCreated }} payment{{ uploadResult.summary.paymentsCreated === 1 ? '' : 's' }} created</div>
          </div>
        </div>

        <!-- Plain message (if API returns just a message string) -->
        <div v-else-if="uploadResult.message" class="result-message">
          {{ uploadResult.message }}
        </div>

        <!-- Per-row errors -->
        <div v-if="hasUploadErrors" class="result-errors">
          <div class="result-errors-title">&#x26A0; {{ uploadResult.errors.length }} row{{ uploadResult.errors.length === 1 ? '' : 's' }} had issues — fix and re-upload</div>
          <table class="errors-table">
            <thead>
              <tr>
                <th>Row</th>
                <th>Field</th>
                <th>Issue</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(err, i) in uploadResult.errors" :key="i">
                <td>{{ err.row }}</td>
                <td>{{ err.field || '-' }}</td>
                <td>{{ err.message }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <button class="result-close" @click="uploadResult = null">&#x2715;</button>
      </div>
    </div>

    <!-- Search -->
    <div class="search-filter-section">
      <div class="search-bar">
        <span class="search-icon">&#x1F50D;</span>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search categories..."
          class="search-input"
          @input="handleSearch"
        />
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading categories...</p>
    </div>

    <!-- Categories Table -->
    <div v-else class="table-container">
      <table class="data-table">
        <thead>
          <tr>
            <th>Category</th>
            <th>Description</th>
            <th class="text-right">Total Spent</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="filteredCategories.length === 0">
            <td colspan="4" class="empty-state">No categories found. Click "Add Category" to create one.</td>
          </tr>
          <tr v-for="category in filteredCategories" :key="category.id" class="data-row">
            <td class="description-col">{{ category.name }}</td>
            <td class="vendor-col">{{ category.description || '-' }}</td>
            <td class="amount-col text-right">${{ (category.totalSpent || 0).toLocaleString() }}</td>
            <td class="actions-col">
              <button class="action-btn edit-btn" title="Edit" @click="openEditModal(category)">&#x270F;&#xFE0F;</button>
              <button class="action-btn delete-btn" title="Delete" @click="confirmDelete(category)">&#x1F5D1;&#xFE0F;</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Add/Edit Category Modal -->
    <AddCategoryModal
      :isOpen="isCategoryModalOpen"
      :category="categoryToEdit"
      @close="closeCategoryModal"
      @create="handleCreateCategory"
      @update="handleUpdateCategory"
    />

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteConfirm" class="modal-overlay" @click.self="cancelDelete">
      <div class="confirm-modal">
        <h3>Delete Category</h3>
        <p>Are you sure you want to delete "{{ categoryToDelete?.name }}"?</p>
        <div class="confirm-actions">
          <button class="btn-cancel" @click="cancelDelete">Cancel</button>
          <button class="btn-delete" @click="handleDelete" :disabled="loading">
            {{ loading ? 'Deleting...' : 'Delete' }}
          </button>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import AddCategoryModal from '@/components/AddCategoryModal.vue'
import bulkUploadService from '@/services/bulkUpload.service'

export default {
  name: 'Categories',
  components: {
    AddCategoryModal
  },
  data() {
    return {
      tabs: [
        { name: 'Expenses', route: '/expenses' },
        { name: 'Payments', route: '/payments' },
        { name: 'Vendors', route: '/vendors' },
        { name: 'Categories', route: '/categories' }
      ],
      searchQuery: '',
      isCategoryModalOpen: false,
      categoryToEdit: null,
      showDeleteConfirm: false,
      categoryToDelete: null,
      searchTimeout: null,
      isDragging: false,
      selectedFile: null,
      uploading: false,
      uploadResult: null
    }
  },
  computed: {
    ...mapGetters('category', ['categories', 'loading']),
    activeTab() {
      const routeToTab = {
        '/expenses': 'Expenses',
        '/payments': 'Payments',
        '/vendors': 'Vendors',
        '/categories': 'Categories'
      }
      return routeToTab[this.$route.path] || 'Categories'
    },
    totalCategories() {
      return this.categories.length
    },
    totalVendors() {
      return this.categories.reduce((sum, c) => sum + (c.vendorCount || 0), 0) || this.categories.length
    },
    totalExpenses() {
      return this.categories.reduce((sum, c) => sum + (c.totalSpent || 0), 0)
    },
    filteredCategories() {
      if (!this.searchQuery) return this.categories
      const query = this.searchQuery.toLowerCase()
      return this.categories.filter(c =>
        (c.name || '').toLowerCase().includes(query) ||
        (c.description || '').toLowerCase().includes(query)
      )
    },
    hasUploadErrors() {
      return this.uploadResult && Array.isArray(this.uploadResult.errors) && this.uploadResult.errors.length > 0
    }
  },
  methods: {
    ...mapActions('category', ['fetchCategories', 'createCategory', 'updateCategory', 'deleteCategory']),

    async loadData() {
      const params = {}
      if (this.searchQuery) {
        params.search = this.searchQuery
      }
      await this.fetchCategories(params)
    },

    handleSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.loadData()
      }, 300)
    },

    openCategoryModal() {
      this.categoryToEdit = null
      this.isCategoryModalOpen = true
    },

    openEditModal(category) {
      this.categoryToEdit = category
      this.isCategoryModalOpen = true
    },

    closeCategoryModal() {
      this.isCategoryModalOpen = false
      this.categoryToEdit = null
    },

    async handleCreateCategory(categoryData) {
      try {
        await this.createCategory(categoryData)
        this.closeCategoryModal()
      } catch (error) {
        console.error('Create category failed:', error)
      }
    },

    async handleUpdateCategory({ id, payload }) {
      try {
        await this.updateCategory({ id, payload })
        this.closeCategoryModal()
      } catch (error) {
        console.error('Update category failed:', error)
      }
    },

    confirmDelete(category) {
      this.categoryToDelete = category
      this.showDeleteConfirm = true
    },

    cancelDelete() {
      this.categoryToDelete = null
      this.showDeleteConfirm = false
    },

    async handleDelete() {
      if (!this.categoryToDelete) return
      try {
        await this.deleteCategory(this.categoryToDelete.id)
        this.cancelDelete()
      } catch (error) {
        console.error('Delete failed:', error)
      }
    },

    navigateToTab(route) {
      this.$router.push(route)
    },

    onFileChange(event) {
      const file = event.target.files[0]
      if (file) {
        this.selectedFile = file
        this.uploadResult = null
      }
      event.target.value = ''
    },

    onFileDrop(event) {
      this.isDragging = false
      const file = event.dataTransfer.files[0]
      if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
        this.selectedFile = file
        this.uploadResult = null
      } else {
        this.$store.dispatch('notification/showError', 'Please drop a valid Excel file (.xlsx or .xls)')
      }
    },

    clearFile() {
      this.selectedFile = null
      this.uploadResult = null
    },

    async uploadFile() {
      if (!this.selectedFile) return
      this.uploading = true
      this.uploadResult = null
      try {
        const result = await bulkUploadService.uploadExpenses(this.selectedFile)
        this.uploadResult = result
        const msg = result?.summary
          ? `Bulk upload complete: ${result.summary.expensesCreated} expenses, ${result.summary.vendorsCreated} vendors, ${result.summary.categoriesCreated} categories, ${result.summary.paymentsCreated} payments created.`
          : (result?.message || 'Upload successful')
        this.$store.dispatch('notification/showSuccess', msg)
        await this.loadData()
      } catch {
        // fetchClient already shows the error notification
      } finally {
        this.uploading = false
      }
    }
  },
  created() {
    this.loadData()
  },
  beforeDestroy() {
    clearTimeout(this.searchTimeout)
  }
}
</script>

<style scoped>
.header-section {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 32px;
}

.title-section {
  flex: 1;
}

.page-title {
  font-size: 36px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 8px;
}

.page-description {
  font-size: 15px;
  color: #9ca3af;
  font-weight: 400;
}

.add-button {
  background: #2563eb;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.add-button:hover {
  background: #1d4ed8;
}

.plus-icon {
  font-size: 18px;
}

/* Stat Cards */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  margin-bottom: 40px;
}

.stat-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
}

.stat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.stat-label {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
}

.stat-icon {
  font-size: 20px;
}

.stat-value {
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 8px;
}

.stat-subtext {
  font-size: 13px;
  color: #9ca3af;
}

/* Tabs */
.tabs-section {
  display: flex;
  gap: 24px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 32px;
  padding-bottom: 0;
}

.tab-btn {
  background: none;
  border: none;
  color: #6b7280;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  padding: 12px 0;
  border-bottom: 2px solid transparent;
  transition: all 0.2s ease;
}

.tab-btn.active {
  color: #2563eb;
  border-bottom-color: #2563eb;
}

.tab-btn:hover {
  color: #1f2937;
}

/* Search and Filters */
.search-filter-section {
  display: flex;
  gap: 16px;
  margin-bottom: 32px;
  align-items: center;
}

.search-bar {
  flex: 1;
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 16px;
  gap: 12px;
}

.search-icon {
  color: #9ca3af;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  color: #1f2937;
}

.search-input::placeholder {
  color: #d1d5db;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background: white;
  border-radius: 8px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e5e7eb;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-state p {
  margin-top: 16px;
  color: #6b7280;
}

/* Empty State */
.empty-state {
  padding: 40px 20px;
  text-align: center;
  color: #6b7280;
}

/* Table */
.table-container {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table thead {
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.data-table th {
  padding: 14px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  letter-spacing: 0.5px;
}

.data-row {
  border-bottom: 1px solid #e5e7eb;
  transition: background 0.2s ease;
}

.data-row:hover {
  background: #f3f4f6;
}

.data-table td {
  padding: 14px 16px;
  font-size: 13px;
  color: #1f2937;
}

.description-col {
  font-weight: 500;
}

.vendor-col {
  color: #6b7280;
}

.amount-col {
  font-weight: 600;
  color: #1f2937;
}

.text-right {
  text-align: right;
}

.actions-col {
  display: flex;
  gap: 8px;
}

.action-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  opacity: 0.6;
  transition: opacity 0.2s ease;
  padding: 4px 8px;
}

.action-btn:hover {
  opacity: 1;
}

.edit-btn:hover {
  color: #2563eb;
}

.delete-btn:hover {
  color: #ef4444;
}

/* Delete Confirmation Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
}

.confirm-modal {
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 400px;
}

.confirm-modal h3 {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 12px;
}

.confirm-modal p {
  color: #6b7280;
  margin-bottom: 24px;
}

.confirm-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.btn-cancel {
  padding: 10px 24px;
  border: 1px solid #e5e7eb;
  background: white;
  color: #1f2937;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.btn-cancel:hover {
  background: #f3f4f6;
}

.btn-delete {
  padding: 10px 24px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.btn-delete:hover {
  background: #dc2626;
}

.btn-delete:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Bulk Upload Card */
.bulk-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 32px;
}

.bulk-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.bulk-card-title {
  display: block;
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
}

.bulk-card-subtitle {
  display: block;
  font-size: 13px;
  color: #6b7280;
}

.download-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #f0fdf4;
  color: #16a34a;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.2s ease;
}

.download-btn:hover {
  background: #dcfce7;
}

/* Drop Zone */
.drop-zone {
  border: 2px dashed #d1d5db;
  border-radius: 10px;
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
  background: #f9fafb;
}

.drop-zone:hover,
.drop-zone--active {
  border-color: #2563eb;
  background: #eff6ff;
}

.drop-zone--has-file {
  border-color: #2563eb;
  background: #eff6ff;
}

.drop-icon {
  font-size: 32px;
}

.drop-text {
  font-size: 14px;
  color: #374151;
  font-weight: 500;
  text-align: center;
}

.file-name {
  color: #2563eb;
  word-break: break-all;
}

.drop-hint {
  font-size: 12px;
  color: #9ca3af;
}

/* Bulk Actions */
.bulk-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
}

.btn-clear {
  padding: 9px 20px;
  border: 1px solid #e5e7eb;
  background: white;
  color: #6b7280;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
}

.btn-clear:hover {
  background: #f3f4f6;
}

.btn-upload {
  padding: 9px 24px;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
}

.btn-upload:hover {
  background: #1d4ed8;
}

.btn-upload:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Result Panel */
.result-panel {
  position: relative;
  margin-top: 20px;
  border-radius: 10px;
  padding: 20px;
  border: 1px solid #bbf7d0;
  background: #f0fdf4;
}

.result-panel--warn {
  border-color: #fde68a;
  background: #fffbeb;
}

.result-close {
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  font-size: 16px;
  color: #6b7280;
  cursor: pointer;
  line-height: 1;
  padding: 2px 6px;
}

.result-close:hover {
  color: #1f2937;
}

.result-summary-title {
  font-size: 14px;
  font-weight: 700;
  color: #15803d;
  margin-bottom: 12px;
}

.result-summary-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.result-chip {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
}

.result-chip--green {
  background: #dcfce7;
  color: #15803d;
}

.result-chip--grey {
  background: #f3f4f6;
  color: #6b7280;
}

.result-message {
  font-size: 14px;
  color: #15803d;
  font-weight: 500;
}

.result-errors {
  margin-top: 16px;
}

.result-errors-title {
  font-size: 13px;
  font-weight: 600;
  color: #92400e;
  margin-bottom: 12px;
}

.errors-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.errors-table th {
  text-align: left;
  padding: 8px 12px;
  background: #fef3c7;
  color: #78350f;
  font-weight: 600;
}

.errors-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #fde68a;
  color: #1f2937;
}

@media (max-width: 768px) {
  .header-section {
    flex-direction: column;
    gap: 16px;
  }

  .search-filter-section {
    flex-direction: column;
  }

  .search-bar {
    width: 100%;
  }

  .bulk-card-header {
    flex-direction: column;
  }

  .download-btn {
    align-self: flex-start;
  }
}
</style>
